<template>
  <q-dialog ref="dialogRef" v-on="dialogListeners" v-bind="dialogProps">
    <q-card ref="cardRef" class="full-width">
      <dialog-header @close="onCloseDialog" title="Confirmar Alterações" sticky />
      <dialog-body>
        <section-card>
          Revise abaixo os dados que foram alterados. Tem certeza que deseja editar essas informações?
          <div class="q-pa-md bg-secondary vix-border q-mt-sm break-word">
            <div class="row q-col-gutter-y-sm">
              <div
                v-for="([field, data], index) in Object.entries(diff)"
                :key="index"
                class="col-12"
              >
                <item-diff
                  :label="getFieldLabel(field)"
                  :old-value="formatFieldValue(field, data?.old, 'old', data)"
                  :new-value="formatFieldValue(field, data?.new, 'new', data)"
                  :is-html="htmlFields.includes(field)"
                >
                  <template #body v-if="arrayFields.includes(field)">
                    <div
                      v-for="el, index in data.removed"
                      :key="index"
                    >
                      <icon-tooltip
                        icon-name="o_do_not_disturb_on"
                        icon-color="negative"
                        icon-size="xs"
                        tooltip-self="center middle"
                        tooltip-anchor="bottom middle"
                        :tooltip-offset="[0, 20]"
                      >
                        Excluído
                      </icon-tooltip>
                      {{ formatFieldValue(field, el, 'old', data.removed) }}
                    </div>
                    <div
                      v-for="el, index in data.added"
                      :key="index"
                    >
                      <icon-tooltip
                        icon-name="add_circle_outline"
                        icon-color="positive"
                        icon-size="xs"
                        tooltip-self="center middle"
                        tooltip-anchor="bottom middle"
                        :tooltip-offset="[0, 20]"
                      >
                        Adicionado
                      </icon-tooltip>
                      {{ formatFieldValue(field, el, 'new', data.added) }}
                    </div>
                  </template>
                </item-diff>
              </div>
            </div>
          </div>
        </section-card>
      </dialog-body>
      <div class="dialog-sticky-footer">
        <q-separator />
        <q-card-actions align="right">
          <q-btn
            @click="onCloseDialog"
            label="Fechar"
            color="accent"
            padding="sm md"
            flat
          />
          <q-btn
            @click="onOKClick"
            label="Salvar"
            color="primary"
            text-color="dark"
            padding="sm md"
          ></q-btn>
        </q-card-actions>
      </div>
    </q-card>
  </q-dialog>
</template>

<script>
import { defineComponent, shallowReactive, computed } from 'vue'
import { useDialogPluginComponent } from 'quasar'
import isEqual from 'lodash.isequal'
import { differenceWith } from 'lodash'

import { useDialog } from 'src/composables/dialog'
import { flattenObject, isPlainObject, isTruthyValue } from 'src/utils'
import cloneDeep from 'lodash.clonedeep'

export default defineComponent({
  name: 'ConfirmationDiffDialog'
})
</script>

<script setup>

const props = defineProps({
  oldObject: {
    type: Object,
    required: true
  },
  newObject: {
    type: Object,
    required: true
  },
  keysToIgnore: {
    type: Array,
    default: () => []
  },
  returnWith: Object,
  fieldsProps: Object,
  jsonFields: {
    type: Array,
    default: () => []
  },
  arrayFields: {
    type: Array,
    default: () => []
  },
  htmlFields: {
    type: Array,
    default: () => []
  }
})

/* const emit = */ defineEmits([...useDialogPluginComponent.emits])

const { dialogRef, onDialogHide, onDialogOK: onOKClick, onDialogCancel: onCancelClick } = useDialogPluginComponent()

const { dialogProps, dialogListeners, onCloseDialog } = shallowReactive(useDialog({ onDialogHide, onCancelClick, onOKClick }))

const valueHasChanged = (oldValue, newValue) => {
  return !isEqual(oldValue, newValue)
}

const diffObjects = (oldObj, newObj, keysToIgnore = [], returnWith = {}, parent = null) => {
  const diff = {}

  for (const [key, newValue] of Object.entries(newObj)) {
    const keyPath = !parent ? key : `${parent}.${key}`

    if (keysToIgnore.includes(keyPath)) {
      continue
    }

    if (!(key in oldObj)) {
      oldObj[key] = Array.isArray(newValue) ? [] : null
    }

    const oldValue = oldObj[key]

    if (!valueHasChanged(oldValue, newValue)) {
      continue
    }

    if (isPlainObject(oldValue) && isPlainObject(newValue)) {
      diff[key] = diffObjects(oldValue, newValue, keysToIgnore, returnWith, keyPath)
    } else if (Array.isArray(oldValue) && Array.isArray(newValue)) {
      diff[key] = {
        removed: differenceWith(oldValue, newValue, isEqual),
        added: differenceWith(newValue, oldValue, isEqual)
      }
    } else {
      diff[key] = {
        old: oldValue,
        new: newValue
      }
    }

    if (!isTruthyValue(diff[key])) {
      delete diff[key]
      continue
    }

    if (keyPath in returnWith) {
      diff[key] = Object.assign(diff[key], returnWith[keyPath])
    }
  }

  return diff
}

const diff = computed(() => {
  const _diff = diffObjects(props.oldObject, props.newObject, props.keysToIgnore, props.returnWith)

  const nestedJsonFieldsData = Object.fromEntries(props.jsonFields.map(field => {
    const nestedKeys = field.split('.')
    const value = nestedKeys.reduce((result, key) => result?.[key], cloneDeep(_diff))

    return [field, value]
  }))

  const flattenData = flattenObject(nestedJsonFieldsData, 1)

  Object.assign(_diff, flattenData)

  props.jsonFields.forEach(field => {
    delete _diff[field]
  })

  return _diff
})

const getField = (field) => {
  const dotLastIndex = String(field).lastIndexOf('.')

  const _field = dotLastIndex === -1
    ? field
    : String(field).slice(dotLastIndex + 1)

  return _field
}

const getFieldLabel = (field) => {
  const _field = getField(field)

  const label = props.fieldsProps?.[_field]?.label

  return (typeof label === 'function' ? label(_field) : label) || _field
}

/**
 * @param {string} field Campo
 * @param {string} value Valor do campo
 * @param {string} type Tipo (pode ser 'old' ou 'new')
 * @param {object} fieldOldNewData Objeto que contém o conteúdo relativo a alteração desse campo.
 * Possui as propriedades old, new e ocasionalmente outras adicionais.
 *
 * @returns {string} Valor formatado
 */
const formatFieldValue = (field, value, type, fieldOldNewData) => {
  const _field = getField(field)

  const format = props.fieldsProps?.[_field]?.format

  return typeof format === 'function'
    ? format(value, type, fieldOldNewData)
    : value
}

</script>
